/**
 * UTM Builder Utility
 *
 * Centralized utility for generating tracking URLs with UTM parameters.
 * Integrates with Usermaven analytics for attribution tracking.
 *
 * @see https://support.google.com/analytics/answer/1033863 - UTM parameter reference
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Standard UTM parameters for campaign tracking
 */
export interface UTMParams {
  /** The referrer (e.g., google, newsletter, email) */
  utm_source: string;
  /** Marketing medium (e.g., cpc, email, social, link) */
  utm_medium?: string;
  /** Campaign name (e.g., spring_sale, launch_2025) */
  utm_campaign?: string;
  /** Identifies specific content/CTA (e.g., hero_button, sidebar_link) */
  utm_content?: string;
  /** Paid search keywords */
  utm_term?: string;
}

/**
 * Ad platform click identifiers captured from URL params
 */
export interface ClickIds {
  /** Google Click ID (primary) */
  gclid?: string;
  /** Google iOS app/web interplay click ID */
  gbraid?: string;
  /** Google iOS web-only click ID */
  wbraid?: string;
  /** Facebook/Meta Click ID */
  fbclid?: string;
  /** OpenAI Ads click ID — auto-appended to every ChatGPT Ads landing URL */
  oppref?: string;
  /**
   * Second OpenAI identifier. The Conversions API schema lists `user.obref`
   * sourced from an `__obref` cookie; the Measurement Pixel doc never mentions
   * it. The two OpenAI docs disagree, so this is read defensively from the
   * cookie only (never required, never fails if absent) and is NOT in
   * CLICK_ID_KEYS — no ad platform documents an `obref` URL parameter.
   */
  obref?: string;
}

/**
 * Preset configurations for common traffic sources
 */
export type UTMPreset =
  | 'email_campaign'
  | 'email_sequence'
  | 'email_transactional'
  | 'blog_internal'
  | 'blog_cta'
  | 'social_organic'
  | 'social_paid'
  | 'cta_hero'
  | 'cta_pricing'
  | 'cta_navbar'
  | 'cta_footer'
  | 'affiliate'
  | 'direct';

/**
 * Options for URL building
 */
export interface BuildUrlOptions {
  /** Preserve existing UTM params in the URL (default: false, overwrites) */
  preserveExisting?: boolean;
  /** Skip adding UTM params entirely (opt-out flag) */
  skipTracking?: boolean;
  /** Additional query params to add (non-UTM) */
  additionalParams?: Record<string, string>;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Standard UTM parameter keys for validation
 */
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/**
 * Maximum length for UTM parameter values (prevents abuse)
 */
const UTM_MAX_VALUE_LENGTH = 200;

/**
 * Ad platform click ID parameter keys read from the URL (Google Ads + Meta + OpenAI Ads)
 */
export const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'oppref'] as const;

/**
 * Cookie configuration for click ID persistence
 * Note: cookie name is 'jds_gclid' for historical reasons but stores all click IDs (gclid, gbraid, wbraid, fbclid, oppref)
 */
const GCLID_COOKIE_NAME = 'jds_gclid';
const GCLID_COOKIE_EXPIRY_DAYS = 90;

/**
 * First-party cookies the OpenAI pixel SDK writes itself, mapped to the
 * ClickIds key they populate. Read defensively: the pixel may be blocked,
 * flag-gated off, or simply not have run, and the two OpenAI docs disagree on
 * whether `__obref` exists at all. Missing is normal, never an error.
 */
const OPENAI_COOKIE_KEYS = [
  ['__oppref', 'oppref'],
  ['__obref', 'obref'],
] as const;

// ============================================================================
// Security Utilities
// ============================================================================

/**
 * Sanitize UTM parameter value to prevent XSS and limit length
 * IMPORTANT: Never include PII in UTM parameters
 *
 * @param value - Raw value from URL
 * @returns Sanitized value safe for storage and display
 */
function sanitizeUTMValue(value: string): string {
  return value
    .slice(0, UTM_MAX_VALUE_LENGTH)
    .replace(/[<>\"'&]/g, ''); // Remove XSS-prone characters
}

/**
 * Get window origin safely (SSR-compatible)
 */
function getOrigin(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

/**
 * Get current search params safely (SSR-compatible)
 */
function getCurrentSearch(): string {
  if (typeof window === 'undefined') return '';
  return window.location.search;
}

/**
 * Preset configurations for common use cases
 */
export const UTM_PRESETS: Record<UTMPreset, Partial<UTMParams>> = {
  // Email presets
  email_campaign: {
    utm_source: 'email',
    utm_medium: 'email',
  },
  email_sequence: {
    utm_source: 'email',
    utm_medium: 'email_sequence',
  },
  email_transactional: {
    utm_source: 'email',
    utm_medium: 'transactional',
  },

  // Blog presets
  blog_internal: {
    utm_source: 'blog',
    utm_medium: 'link',
  },
  blog_cta: {
    utm_source: 'blog',
    utm_medium: 'cta',
  },

  // Social presets
  social_organic: {
    utm_source: 'social',
    utm_medium: 'organic',
  },
  social_paid: {
    utm_source: 'social',
    utm_medium: 'paid',
  },

  // CTA presets (internal navigation)
  cta_hero: {
    utm_source: 'website',
    utm_medium: 'cta',
    utm_content: 'hero',
  },
  cta_pricing: {
    utm_source: 'website',
    utm_medium: 'cta',
    utm_content: 'pricing',
  },
  cta_navbar: {
    utm_source: 'website',
    utm_medium: 'cta',
    utm_content: 'navbar',
  },
  cta_footer: {
    utm_source: 'website',
    utm_medium: 'cta',
    utm_content: 'footer',
  },

  // Affiliate (works alongside Push Lap Growth)
  affiliate: {
    utm_source: 'affiliate',
    utm_medium: 'referral',
  },

  // Direct/fallback
  direct: {
    utm_source: 'direct',
    utm_medium: 'none',
  },
};

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Build a URL with UTM parameters
 *
 * @param baseUrl - The target URL (can include existing query params)
 * @param params - UTM parameters to add
 * @param options - Build options
 * @returns URL string with UTM parameters
 *
 * @example
 * ```ts
 * buildTrackingUrl('https://jdsimplified.com/pricing', {
 *   utm_source: 'email',
 *   utm_medium: 'newsletter',
 *   utm_campaign: 'jan_2025'
 * });
 * // => "https://jdsimplified.com/pricing?utm_source=email&utm_medium=newsletter&utm_campaign=jan_2025"
 * ```
 */
export function buildTrackingUrl(
  baseUrl: string,
  params: Partial<UTMParams>,
  options: BuildUrlOptions = {}
): string {
  const { preserveExisting = false, skipTracking = false, additionalParams = {} } = options;

  // If tracking is disabled, return original URL with only additional params
  if (skipTracking) {
    if (Object.keys(additionalParams).length === 0) {
      return baseUrl;
    }
    const origin = getOrigin();
    if (!origin) return baseUrl; // SSR safety
    const url = new URL(baseUrl, origin);
    Object.entries(additionalParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    return url.toString();
  }

  try {
    // Parse the URL, handling relative URLs
    const origin = getOrigin();
    if (!origin) return baseUrl; // SSR safety
    const url = new URL(baseUrl, origin);

    // Handle existing UTM params
    if (!preserveExisting) {
      // Remove existing UTM params
      UTM_KEYS.forEach(key => url.searchParams.delete(key));
    }

    // Add new UTM params (only if value is provided)
    Object.entries(params).forEach(([key, value]) => {
      if (value && UTM_KEYS.includes(key as typeof UTM_KEYS[number])) {
        // If preserving existing and param exists, skip
        if (preserveExisting && url.searchParams.has(key)) {
          return;
        }
        url.searchParams.set(key, value);
      }
    });

    // Add additional non-UTM params
    Object.entries(additionalParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    return url.toString();
  } catch {
    // If URL parsing fails (e.g., mailto:, tel:), return original
    return baseUrl;
  }
}

/**
 * Build a URL using a preset configuration
 *
 * @param baseUrl - The target URL
 * @param preset - Preset configuration name
 * @param overrides - Optional overrides for the preset
 * @param options - Build options
 * @returns URL string with UTM parameters
 *
 * @example
 * ```ts
 * buildUrlFromPreset('https://jdsimplified.com/signup', 'email_campaign', {
 *   utm_campaign: 'welcome_series'
 * });
 * ```
 */
export function buildUrlFromPreset(
  baseUrl: string,
  preset: UTMPreset,
  overrides: Partial<UTMParams> = {},
  options: BuildUrlOptions = {}
): string {
  const presetParams = UTM_PRESETS[preset];
  const mergedParams = { ...presetParams, ...overrides };
  return buildTrackingUrl(baseUrl, mergedParams, options);
}

/**
 * Extract UTM parameters from a URL or the current page
 *
 * @param url - Optional URL to extract from (defaults to current page)
 * @returns Partial UTM params object (only includes present params)
 *
 * @example
 * ```ts
 * // From current page
 * const params = extractUTMParams();
 *
 * // From specific URL
 * const params = extractUTMParams('https://example.com?utm_source=email');
 * ```
 */
export function extractUTMParams(url?: string): Partial<UTMParams> {
  try {
    const searchParams = url
      ? new URL(url).searchParams
      : new URLSearchParams(getCurrentSearch());

    const params: Partial<UTMParams> = {};

    UTM_KEYS.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        // Sanitize value to prevent XSS and limit length
        params[key] = sanitizeUTMValue(value);
      }
    });

    return params;
  } catch {
    return {};
  }
}

/**
 * Check if a URL has any UTM parameters
 *
 * @param url - URL to check (defaults to current page)
 * @returns Boolean indicating presence of UTM params
 */
export function hasUTMParams(url?: string): boolean {
  const params = extractUTMParams(url);
  return Object.keys(params).length > 0;
}

/**
 * Merge UTM params with conflict resolution
 *
 * @param existing - Existing UTM params
 * @param incoming - New UTM params to merge
 * @param strategy - Conflict resolution: 'incoming' (default) or 'existing'
 * @returns Merged UTM params
 */
export function mergeUTMParams(
  existing: Partial<UTMParams>,
  incoming: Partial<UTMParams>,
  strategy: 'incoming' | 'existing' = 'incoming'
): Partial<UTMParams> {
  if (strategy === 'existing') {
    return { ...incoming, ...existing };
  }
  return { ...existing, ...incoming };
}

/**
 * Validate UTM params structure
 *
 * @param params - Params to validate
 * @returns Boolean indicating if params are valid
 */
export function validateUTMParams(params: Partial<UTMParams>): boolean {
  // utm_source is required for valid tracking
  if (!params.utm_source) {
    return false;
  }

  // All keys must be valid UTM keys
  return Object.keys(params).every(key =>
    UTM_KEYS.includes(key as typeof UTM_KEYS[number])
  );
}

// ============================================================================
// Storage & Persistence
// ============================================================================

const UTM_STORAGE_KEY = 'jds_utm_params';
const UTM_STORAGE_EXPIRY_KEY = 'jds_utm_expiry';
const UTM_DEFAULT_EXPIRY_HOURS = 24; // Persist UTM for 24 hours

/**
 * Store UTM params in sessionStorage for cross-page persistence
 *
 * @param params - UTM params to store
 * @param expiryHours - Hours until expiry (default: 24)
 */
export function storeUTMParams(
  params: Partial<UTMParams>,
  expiryHours: number = UTM_DEFAULT_EXPIRY_HOURS
): void {
  try {
    const expiry = Date.now() + (expiryHours * 60 * 60 * 1000);
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    sessionStorage.setItem(UTM_STORAGE_EXPIRY_KEY, expiry.toString());
  } catch {
    // Storage might be unavailable (private browsing, etc.)
  }
}

/**
 * Retrieve stored UTM params (if not expired)
 *
 * @returns Stored UTM params or empty object if expired/missing
 */
export function getStoredUTMParams(): Partial<UTMParams> {
  try {
    const expiryStr = sessionStorage.getItem(UTM_STORAGE_EXPIRY_KEY);
    if (!expiryStr || Date.now() > parseInt(expiryStr, 10)) {
      clearStoredUTMParams();
      return {};
    }

    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return {};

    try {
      const parsed = JSON.parse(stored);
      // Validate it's an object with expected structure
      if (typeof parsed !== 'object' || parsed === null) {
        clearStoredUTMParams();
        return {};
      }
      return parsed;
    } catch {
      // Clear corrupted data
      clearStoredUTMParams();
      return {};
    }
  } catch {
    return {};
  }
}

/**
 * Clear stored UTM params
 */
export function clearStoredUTMParams(): void {
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
    sessionStorage.removeItem(UTM_STORAGE_EXPIRY_KEY);
  } catch {
    // Ignore storage errors
  }
}

// ----------------------------------------------------------------------------
// First-touch landing page
// ----------------------------------------------------------------------------

/**
 * The path the visitor ARRIVED on, captured once per tab session.
 *
 * WHY THIS EXISTS: `ad_click_attributions.landing_page` used to be filled from
 * `window.location.pathname` at POST time, and the POST only ever happens from
 * AuthForm sign-in / sign-up or PricingPage checkout — i.e. AFTER navigation. So
 * the column recorded the page the user CONVERTED on, never the page the ad
 * pointed at. Verified against prod 2026-09-07: all 162 rows ever written hold
 * `/auth` or `/pricing`, and nothing else. Nothing errored and every row looked
 * well-formed — the field was simply a constant, which is why it went unnoticed.
 *
 * FIRST TOUCH IS THE WHOLE POINT. A last-write-wins store re-records `/auth` the
 * moment the visitor navigates there and reproduces the original bug exactly,
 * with more code. The write below is therefore guarded: the first path of the
 * session wins and every later arrival is ignored.
 *
 * Pinned by 'sticks to the FIRST path across later navigations' and 'returns the
 * ALREADY-STORED path' in __tests__/attribution.test.ts — verified by mutation,
 * not by reading: deleting the guard turns both red. Note those tests live in
 * attribution.test.ts, NOT in utm-builder.test.ts alongside this code; all 52
 * tests there stay green when the guard is removed. If you ever split or trim
 * that file, this invariant loses its only guard silently.
 *
 * Lifetime is the sessionStorage tab session — deliberately NOT the UTM blob's
 * 24h expiry. An organic visitor never writes the UTM keys at all (there are no
 * params to store), so hanging the landing path off that expiry would leave it
 * unreadable for exactly the traffic where it is the only signal we have.
 */
const LANDING_PAGE_STORAGE_KEY = 'jds_landing_page';

/**
 * Path only — never the query string. UTMs are stored separately and a raw
 * query string is the most likely place for a stray identifier to ride along.
 *
 * @returns The normalized same-origin path, or `null` if the input is not one.
 *
 * `null` rather than a `'/'` fallback, because the caller is a FIRST-WRITE-WINS
 * store: a rejected value that returns `'/'` gets written, permanently consuming
 * the one slot the real landing page needed. Returning null lets the caller
 * decline to write and take the next observation instead.
 *
 * THE `//` CHECK IS LOAD-BEARING, NOT BELT-AND-BRACES. `//evil.example/phish`
 * starts with `'/'`, so a bare `startsWith('/')` accepts it — and it is
 * reachable: `https://jdsimplified.com//evil.example/phish` yields exactly that
 * `location.pathname`, the GCS SPA fallback serves index.html for it, and this
 * capture runs before the router falls through to NotFoundPage. It would then be
 * POSTed verbatim into `ad_click_attributions.landing_page`, whose server-side
 * handling is a 2000-char truncation and nothing else. Today the only thing
 * stopping it becoming an off-site absolute URL is `normalizeSourceUrl` in
 * services/payment/src/lib/openai-ads.ts, which guards the same case for its own
 * unrelated reasons, in another service, with no test tying it to this producer.
 * Any future consumer doing `new URL(landing_page, SITE_ORIGIN)` resolves to the
 * attacker's origin. Guard at the producer.
 */
function normalizeLandingPath(path: string): string | null {
  const pathOnly = path.split('?')[0].split('#')[0];
  // Protocol-relative ('//host/x') and scheme-relative ('/\\host/x', which some
  // browsers normalize to '//') are both absolute-with-an-origin, not paths.
  if (!pathOnly.startsWith('/')) return null;
  if (pathOnly.startsWith('//') || pathOnly.startsWith('/\\')) return null;
  return pathOnly.slice(0, 200);
}

/**
 * Record the landing path IF none has been recorded this session.
 *
 * @returns The session's first-touch path — the one already stored when there
 *          was one, NOT necessarily the path passed in.
 */
export function storeFirstTouchLandingPage(path: string): string {
  const normalized = normalizeLandingPath(path);
  try {
    // `=== null` rather than a falsy check: a stored empty string is still a
    // recorded first touch, and `!''` would silently let the next path overwrite
    // it. normalizeLandingPath cannot return '' today; this survives it changing.
    const existing = sessionStorage.getItem(LANDING_PAGE_STORAGE_KEY);
    if (existing !== null) return existing;
    // A rejected path does NOT consume the first-touch slot — see
    // normalizeLandingPath. Writing a placeholder here would burn the one write
    // this store gets and lock in a value nobody landed on.
    if (normalized === null) return '/';
    sessionStorage.setItem(LANDING_PAGE_STORAGE_KEY, normalized);
  } catch {
    // Storage unavailable (private browsing, blocked site data) — the caller
    // falls back to the live pathname, which is the pre-2026-09-07 behaviour.
  }
  return normalized ?? '/';
}

/**
 * Overwrite the landing page because a NEW campaign episode has begun.
 *
 * The ONLY legitimate caller is `captureArrival`, gated on `isNewCampaignEpisode`.
 * Calling this on plain navigation converts the store to last-write-wins and
 * puts `/auth` straight back in the column — the original bug, restored by the
 * code that exists to prevent it. It is exported for tests and for that gate,
 * not as a general-purpose setter.
 *
 * A rejected path does NOT clobber a good stored value: re-arming with garbage
 * would be strictly worse than keeping the previous episode's real path.
 *
 * ASYMMETRY WITH `storeFirstTouchLandingPage`, and it is intentional. On a
 * rejected path that one returns `'/'` while this one returns the STORED value.
 * They differ because their situations differ: first-touch has nothing to
 * preserve, a re-arm does. `captureArrival` surfaces whichever ran as a single
 * `landingPage`, which the hook also reports to Usermaven, so the two can name
 * different values for the same input state — correct in each case, but do not
 * "harmonize" them into one return without re-reading both reasons.
 */
export function rearmLandingPage(path: string): string {
  const normalized = normalizeLandingPath(path);
  if (normalized === null) return getStoredLandingPage() ?? '/';
  try {
    sessionStorage.setItem(LANDING_PAGE_STORAGE_KEY, normalized);
  } catch {
    // Storage unavailable — caller falls back to the live pathname.
  }
  return normalized;
}

/** The first-touch landing path for this tab session, or null if none stored. */
export function getStoredLandingPage(): string | null {
  try {
    return sessionStorage.getItem(LANDING_PAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Clear the stored first-touch path. Exists for tests and for a future
 *  "forget me" path; nothing in the app calls it during normal operation. */
export function clearStoredLandingPage(): void {
  try {
    sessionStorage.removeItem(LANDING_PAGE_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

// `captureAndStoreLandingPage(path?)` used to live here as the public entry
// point. It was DELETED when `captureArrival` took over, rather than kept as a
// convenience wrapper: it recorded the landing page WITHOUT the episode check,
// so calling it would silently pin the first path of the tab forever and
// reintroduce the mixed-episode row. It also read as the obvious public API,
// which made it the easiest wrong call to make — an unguarded twin sitting next
// to a guarded one (.claude/rules/propagate-the-fix.md). Use `captureArrival`.

/**
 * Capture UTM params from current URL and store them
 * Call this on app initialization to persist attribution across navigation
 *
 * @returns The captured params (empty if none present)
 */
export function captureAndStoreUTMParams(): Partial<UTMParams> {
  const currentParams = extractUTMParams();

  if (Object.keys(currentParams).length > 0) {
    // New UTM params in URL - store them (overwrite existing)
    storeUTMParams(currentParams);
    return currentParams;
  }

  // No new params - return stored ones
  return getStoredUTMParams();
}

// ============================================================================
// Helper Functions for Common Scenarios
// ============================================================================

/**
 * Generate a tracking URL for email campaigns
 *
 * @param baseUrl - Target URL
 * @param campaignName - Name of the email campaign
 * @param emailType - Type of email (campaign, sequence, transactional)
 * @param content - Optional content identifier
 */
export function generateEmailTrackingUrl(
  baseUrl: string,
  campaignName: string,
  emailType: 'campaign' | 'sequence' | 'transactional' = 'campaign',
  content?: string
): string {
  const presetMap = {
    campaign: 'email_campaign',
    sequence: 'email_sequence',
    transactional: 'email_transactional',
  } as const;

  return buildUrlFromPreset(baseUrl, presetMap[emailType], {
    utm_campaign: campaignName,
    utm_content: content,
  });
}

/**
 * Generate a tracking URL for blog content
 *
 * @param baseUrl - Target URL
 * @param postSlug - Blog post slug for attribution
 * @param isCta - Whether this is a CTA link (vs inline link)
 */
export function generateBlogTrackingUrl(
  baseUrl: string,
  postSlug: string,
  isCta: boolean = false
): string {
  return buildUrlFromPreset(
    baseUrl,
    isCta ? 'blog_cta' : 'blog_internal',
    {
      utm_campaign: 'blog_content',
      utm_content: postSlug,
    }
  );
}

// ============================================================================
// Click ID (gclid/gbraid/wbraid) Functions
// ============================================================================

/**
 * Extract Google Ads click IDs from URL params
 *
 * @param url - Optional URL to extract from (defaults to current page)
 * @returns Click IDs found in URL
 */
export function extractClickIds(url?: string): ClickIds {
  try {
    const searchParams = url
      ? new URL(url).searchParams
      : new URLSearchParams(getCurrentSearch());

    const ids: ClickIds = {};
    CLICK_ID_KEYS.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        ids[key] = sanitizeUTMValue(value);
      }
    });
    return ids;
  } catch {
    return {};
  }
}

/**
 * Set a first-party cookie for gclid persistence (90-day expiry)
 *
 * @param clickIds - Click IDs to store
 */
export function storeClickIdCookie(clickIds: ClickIds): void {
  if (typeof document === 'undefined') return;
  try {
    const expires = new Date(Date.now() + GCLID_COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    const value = JSON.stringify(clickIds);
    document.cookie = `${GCLID_COOKIE_NAME}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
  } catch {
    // Cookie storage unavailable
  }
}

/**
 * Get stored click IDs from cookie
 *
 * @returns Stored click IDs or empty object
 */
export function getStoredClickIds(): ClickIds {
  if (typeof document === 'undefined') return {};
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${GCLID_COOKIE_NAME}=([^;]*)`));
    if (!match) return {};
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed;
  } catch {
    return {};
  }
}

/**
 * Is there anything worth writing to `ad_click_attributions`?
 *
 * True when any ad-platform click id or a `utm_source` is present. Derived
 * from CLICK_ID_KEYS rather than a hand-listed set, so adding a URL click-id
 * key cannot leave this predicate silently behind.
 *
 * `obref` is DELIBERATELY EXCLUDED, and it is the one thing to get right here.
 * This predicate has to mirror the server's accept gate in
 * `services/payment/src/routes/attribution.ts`, which reads
 * gclid/gbraid/wbraid/fbclid/utm_source/oppref and does not mention `obref` at
 * all. Counting obref here would make an obref-only visitor POST a request the
 * server answers with a 400 — a guaranteed failure for a request we chose to
 * send. obref still travels IN the payload whenever it exists; it just cannot
 * be the sole reason to send one.
 *
 * If the server ever adds obref to that gate, add it back here in the same
 * change — the two gates are a matched pair with no compile-time link.
 *
 * NOTE this is only ONE of the two reasons to POST attribution. A GPC browser
 * must POST even when this returns false, so the server can record the
 * opt-out. See `buildAttributionRequest` in ./attribution.ts — do not use this
 * predicate on its own as the send gate.
 */
export function hasAttributionData(
  clickIds: ClickIds,
  utmParams: Partial<UTMParams>
): boolean {
  return CLICK_ID_KEYS.some((key) => !!clickIds[key]) || !!utmParams.utm_source;
}

/**
 * Every click id available from persistent storage, merged.
 *
 * Use this — not `getStoredClickIds()` alone — anywhere a click id is being
 * put into an attribution payload. `getStoredClickIds()` reads only our own
 * `jds_gclid` cookie and so silently drops `obref`, which never appears as a
 * URL parameter and therefore only ever lives in OpenAI's own cookie.
 *
 * Read-only: unlike `captureAndStoreClickIds()` this writes nothing, so it is
 * safe to call from a submit handler.
 */
export function getAllStoredClickIds(): ClickIds {
  // Our own cookie wins: it holds the URL-captured last-touch value.
  return { ...readOpenAiClickIdCookies(), ...getStoredClickIds() };
}

/**
 * Read the OpenAI pixel's own first-party cookies (`__oppref`, `__obref`).
 *
 * This is a defensive FALLBACK, not the primary capture path. `oppref` arrives
 * as a URL parameter on every ChatGPT Ads landing and is captured into our own
 * 90-day `jds_gclid` cookie like every other click id. These cookies only help
 * when our capture did not see the URL (a direct return visit within the
 * pixel's own cookie lifetime, say). Anything found here is therefore LOWER
 * precedence than a URL param or our stored cookie.
 *
 * @returns Whichever OpenAI cookies exist — `{}` is the normal result
 */
export function readOpenAiClickIdCookies(): ClickIds {
  if (typeof document === 'undefined') return {};

  const ids: ClickIds = {};
  for (const [cookieName, key] of OPENAI_COOKIE_KEYS) {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|; )${cookieName}=([^;]*)`));
      if (!match || !match[1]) continue;
      const value = sanitizeUTMValue(decodeURIComponent(match[1]));
      if (value) ids[key] = value;
    } catch {
      // Malformed percent-encoding or cookies unavailable — skip this one and
      // keep reading the others. A bad __oppref must not lose a good __obref.
    }
  }
  return ids;
}

/**
 * Capture click IDs from URL and store in 90-day cookie.
 * New URL params overwrite existing cookie (last-touch for fresh clicks).
 * Falls back to stored cookie when no click IDs are in the URL, and to the
 * OpenAI pixel's own cookies for any OpenAI id neither source supplied.
 *
 * @returns The active click IDs (from URL, our cookie, or OpenAI's cookies)
 */
export function captureAndStoreClickIds(): ClickIds {
  // Lowest precedence — only fills keys the authoritative sources lack.
  const openAiCookieIds = readOpenAiClickIdCookies();

  const currentIds = extractClickIds();

  if (Object.keys(currentIds).length > 0) {
    // New click IDs in URL — store them (overwrites existing).
    // Only the URL-sourced ids are persisted; OpenAI's cookies are already
    // persistent on their own and re-read on every call.
    storeClickIdCookie(currentIds);
    return { ...openAiCookieIds, ...currentIds };
  }

  // No new IDs — return stored ones, backfilled from OpenAI's cookies
  return { ...openAiCookieIds, ...getStoredClickIds() };
}

// ============================================================================
// Arrival capture — UTM + click ids + landing page, as ONE unit
// ============================================================================

/**
 * Has a NEW campaign episode begun on this URL?
 *
 * "Episode" is the unit that matters, because the three attribution stores have
 * three different lifetimes and are read as one story:
 *
 *   - `jds_landing_page` — sessionStorage, tab-scoped, never expires
 *   - `jds_utm_params`   — sessionStorage, 24h, last-write-wins
 *   - `jds_gclid` cookie — 90 days
 *
 * Left to themselves they drift apart. A returning visitor with a stale tab who
 * clicks a ChatGPT ad produced a row reading `utm_source=chatgpt` +
 * `oppref=<real click id>` + `landing_page=/` — an ad-attributed conversion
 * pointing at a page the ad never linked to, assembled from two different
 * visits. That is worse than the uniform `/auth` this whole fix replaced,
 * because `/auth` was obviously broken and this looks plausible.
 *
 * So the landing page is re-armed exactly when the campaign identity changes,
 * and never otherwise:
 *
 *   - a click id in the URL is absent-or-different in storage, OR
 *   - `utm_source` / `utm_campaign` in the URL differ from what is stored.
 *
 * WHAT MUST NOT TRIGGER IT, and why each is safe:
 *   - Plain in-session navigation. The URL carries no campaign params at all, so
 *     both checks see an empty URL side and short-circuit. This is the case that
 *     matters most: make it re-arm and the store is last-write-wins again and the
 *     column goes back to `/auth`, while every other test still passes.
 *   - A refresh with the same params still in the URL. Values are identical to
 *     storage, so it is the same episode, not a new one.
 *
 * Click-id keys are derived from CLICK_ID_KEYS rather than hand-listed, so a
 * sixth key added there re-arms automatically — same reasoning as
 * `hasAttributionData`, and pinned by a test that iterates CLICK_ID_KEYS.
 *
 * PURE: reads only. It has to be, because it compares the URL against storage
 * and every writer in this file overwrites the very values being compared.
 * Calling any `captureAndStore*` before this makes it permanently answer false.
 */
export function isNewCampaignEpisode(): boolean {
  const urlClickIds = extractClickIds();
  const storedClickIds = getAllStoredClickIds();

  const clickIdChanged = CLICK_ID_KEYS.some(
    (key) => !!urlClickIds[key] && urlClickIds[key] !== storedClickIds[key]
  );
  if (clickIdChanged) return true;

  const urlUtm = extractUTMParams();

  // ---------------------------------------------------------------------
  // A URL WITHOUT utm_source IS NOT AN INBOUND CAMPAIGN LINK. Bail early.
  //
  // This line is the difference between a fix and a re-run of the original
  // bug, and the first version of this predicate did not have it.
  //
  // Internal CTAs in this app navigate through `buildTrackingUrl` and inject a
  // HARDCODED `utm_campaign` into the destination — e.g. BarPracticePage's
  // `buildSignupUrl` sends you to
  // `/auth?tab=signup&utm_campaign=nextgen_prep&utm_content=hero`, and
  // HomepagePricingSection / PricingPage / BarPracticeTrialPage /
  // StartNowBundleCard do the same. React Router preserves that query string
  // into `window.location.search`. So "plain in-session navigation" in this
  // product is NOT param-free: clicking the signup button presents a URL whose
  // `utm_campaign` differs from whatever is stored, which without this check
  // reads as a new episode and re-arms the landing page to `/auth`.
  //
  // That is the exact pre-fix value, restored by the mechanism built to prevent
  // it, and reached hardest by the traffic we care about most: a click-id-only
  // ad (ChatGPT appends `oppref` and UTM tagging is optional) or any visitor
  // past the 24h UTM expiry has an EMPTY stored blob, so the CTA's hardcoded
  // campaign always differs. Caught in review, not by tests — the negative
  // tests navigated to bare `/bar/practice` and `/auth`, which short-circuit on
  // the `!!urlUtm[key]` check below and therefore never exercised this path.
  //
  // A real inbound campaign link always carries `utm_source` (it is what
  // `hasAttributionData` itself gates on); an internally-generated CTA link
  // carries `utm_source` only when it inherited one from storage, in which case
  // it equals the stored value and is correctly not a new episode. An ad that
  // tags no UTMs at all is caught by the click-id half above, which this
  // early-return does not touch.
  // ---------------------------------------------------------------------
  if (!urlUtm.utm_source) return false;

  const storedUtm = getStoredUTMParams();

  // utm_source and utm_campaign only.
  //
  // EXCLUDED, both deliberately:
  //   - `utm_content` / `utm_term` vary per CTA WITHIN one campaign, so
  //     counting them would re-arm on an ordinary second click of the same ad.
  //   - The landing PATH. Two arrivals under one source+campaign on different
  //     paths are one episode by this definition, so a Listmonk sequence that
  //     reuses a campaign name across sends (see `generateEmailTrackingUrl`)
  //     keeps the first landing page.
  //
  //     ACCEPTED, NOT OVERLOOKED. Blast radius is email sequences only: every
  //     paid ad is still detected — a UTM-tagged one by its own `utm_source`,
  //     an untagged one by the click-id half above, which runs before the
  //     `utm_source` early-return. Per-landing-page comparison of AD traffic,
  //     the reason this column exists, is unaffected.
  //
  //     ADDING PATH TO IDENTITY IS A REGRESSION, NOT AN IMPROVEMENT, and the
  //     reason is NOT the one you would guess. "Same source+campaign, different
  //     path" is the signature of ORDINARY INTERNAL CTA NAVIGATION in this app,
  //     not of a new email episode: BarPracticeTrialPage (~:139),
  //     BarPracticePage (~:394) and StartNowBundleCard (~:157) all call
  //     `buildTrackingUrl('/auth?tab=signup', { utm_campaign: '<hardcoded>',
  //     utm_content: action, ...getStoredUTMParams() })` with the stored blob
  //     spread LAST — so on a UTM-tagged ad the CTA URL carries the SAME source
  //     and campaign that are already stored. Path in identity would therefore
  //     see `/auth` != `/bar/practice/trial` and re-arm to `/auth` on every
  //     UTM-tagged ad conversion: the exact pre-2026-09-07 defect, all 162 prod
  //     rows reading `/auth` or `/pricing`, restored on the traffic this column
  //     is for.
  //
  //     REVISIT TRIGGER (lead decision 2026-09-07, corrected in review the same
  //     day): if email becomes a material share of tracked conversions, path can
  //     join episode identity only AFTER internal CTA navigation is
  //     distinguishable from an inbound arrival — stop forwarding stored UTMs
  //     from those three builders, or gate on an external `document.referrer`,
  //     or set an explicit arrival marker. Fix that first; the identity change
  //     is the second step, never the first.
  //
  //     TWO THINGS THIS COMMENT USED TO SAY THAT WERE WRONG, recorded because
  //     both were plausible and one was actively dangerous:
  //       - "the effect keys on `location.search`, so path in identity would be
  //         a silent no-op". The dep array claim is TRUE for in-SPA navigation
  //         (`use-utm-tracking.tsx` ~:137) but it is NOT why path is excluded,
  //         and it is false for the case it was invoked about: an email click is
  //         a DOCUMENT LOAD, so the effect runs on mount regardless of its deps
  //         and observes the new pathname fine. Telling the next reader that a
  //         harmful change is a harmless no-op is an invitation to try it.
  //       - "every paid ad carries its own `utm_source`". Untagged ads are the
  //         normal case for Meta and ChatGPT (see the clear-stale-blob note in
  //         `captureArrival`); they are caught by the click-id half, not by a
  //         `utm_source` every ad supposedly has.
  return (['utm_source', 'utm_campaign'] as const).some(
    (key) => !!urlUtm[key] && urlUtm[key] !== storedUtm[key]
  );
}

/** Everything captured from one arrival, plus whether it began a new episode. */
export interface ArrivalCapture {
  utmParams: Partial<UTMParams>;
  clickIds: ClickIds;
  landingPage: string;
  isNewEpisode: boolean;
}

/**
 * Capture UTM params, click ids, and the landing page from one arrival.
 *
 * ONE FUNCTION ON PURPOSE. These three values ship together in a single
 * `ad_click_attributions` row and are read as one story, so they are captured as
 * one unit rather than by three calls that usually agree. The ordering below is
 * the whole reason this exists:
 *
 *   1. DETECT — `isNewCampaignEpisode()` compares the URL against storage.
 *   2. WRITE  — the UTM blob, the click-id cookie, and the landing page.
 *
 * Inverting those two steps does not fail, it just makes the detector answer
 * false forever: `captureAndStoreUTMParams` and `captureAndStoreClickIds` both
 * overwrite the stored side of the comparison. The result would be a landing
 * page that never re-arms, no error, and a plausible wrong row — which is the
 * defect this function was written to close. Do not reorder.
 *
 * @param path Defaults to the live pathname. `useUTMTracking` passes the
 *             router's location so the value does not depend on what `window`
 *             happens to read at that tick.
 */
export function captureArrival(path?: string): ArrivalCapture {
  const currentPath =
    path ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  // ---- 1. DETECT. Every read here must precede every write below. ----
  const isNewEpisode = isNewCampaignEpisode();
  const urlHasUtm = Object.keys(extractUTMParams()).length > 0;

  // ---- 2. WRITE. ----

  // A new episode that carries NO UTMs of its own must not inherit the previous
  // episode's. `captureAndStoreUTMParams` returns the stored blob unchanged when
  // the URL has no utm_* keys, which is right for navigation within an episode
  // and wrong across one: arriving on `?fbclid=...` after a `utm_source=chatgpt`
  // visit produced a row reading `landing_page=/bar/practice/trial` + a real
  // `fbclid` + `utm_source=chatgpt` — the click id and landing page from
  // episode 2, the campaign from episode 1.
  //
  // That is the same mixed-row defect the landing-page re-arm exists to fix,
  // rotated onto a different field, and it now looks MORE plausible because the
  // landing page beside it is correct. The click-id half is already clean:
  // `storeClickIdCookie` replaces the whole cookie, so a chatgpt->google episode
  // correctly yields `oppref: null`. Only the UTM blob was sticky.
  //
  // Untagged ads are the normal case for Meta and for ChatGPT (`oppref` is
  // appended automatically; UTM tagging is optional), so this is the common
  // path, not an edge. `hasAttributionData` still passes on the click id, so the
  // row is still POSTed — it just no longer carries a campaign from another
  // visit. This is also the consumer for `isNewEpisode`: computing it and
  // returning it without acting on it would be an orphaned producer.
  if (isNewEpisode && !urlHasUtm) clearStoredUTMParams();

  const utmParams = captureAndStoreUTMParams();
  const clickIds = captureAndStoreClickIds();
  const landingPage = isNewEpisode
    ? rearmLandingPage(currentPath)
    : storeFirstTouchLandingPage(currentPath);

  return { utmParams, clickIds, landingPage, isNewEpisode };
}

/**
 * Check if a URL should have tracking added
 * Excludes external domains, special protocols, and anchor links
 *
 * @param url - URL to check
 * @returns Boolean indicating if tracking should be added
 */
export function shouldAddTracking(url: string): boolean {
  // Skip special protocols (including data:, blob:, file:)
  if (/^(mailto:|tel:|javascript:|data:|blob:|file:|#)/.test(url)) {
    return false;
  }

  try {
    const origin = getOrigin();
    if (!origin) return false; // SSR safety
    const parsed = new URL(url, origin);

    // Only track links to our domains
    const ourDomains = [
      'jdsimplified.com',
      'dev.jdsimplified.com',
      'admin.jdsimplified.com',
      'localhost',
    ];

    return ourDomains.some(domain =>
      parsed.hostname === domain || parsed.hostname.endsWith('.' + domain)
    );
  } catch {
    // If URL parsing fails, skip tracking
    return false;
  }
}
